import { toast } from '@repo/studio-components-toast'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useConversationQuery } from '../../hooks/use-conversation'
import {
  readAidaConversationSession,
  writeAidaConversationSession,
} from '../../stores/conversation-session'
import { bumpAidaSessionEpoch, useAidaSessionEpochStore } from '../../stores/session-epoch'
import { openAidaOverlay, openAidaOverlayFromElement } from '../../stores/overlay-store'

function selectedConversationId() {
  const session = readAidaConversationSession()
  if (session?.kind === 'selected') {
    return session.id
  }
  return null
}

function openAidaOnChat(conversationId: string) {
  writeAidaConversationSession({ kind: 'selected', id: conversationId })
  bumpAidaSessionEpoch()
  const launcher = document.querySelector<HTMLElement>(
    '[data-aida-floating-button], [data-aida-peek-tab]',
  )
  if (launcher) {
    openAidaOverlayFromElement(launcher)
    return
  }
  openAidaOverlay({ top: 0, left: 0, width: 48, height: 48 })
}

export function useClosedAidaGenerating(overlayOpen: boolean, allowed: boolean) {
  const { t } = useTranslation('components')
  useAidaSessionEpochStore(state => state.epoch)
  const conversationId = allowed ? selectedConversationId() : null
  const conversationQuery = useConversationQuery(conversationId)
  const busy = conversationQuery.data?.busy === true
  const seen = React.useRef<{ id: string | null; busy: boolean } | null>(null)

  React.useEffect(() => {
    const previous = seen.current
    seen.current = { id: conversationId, busy }
    if (
      !previous ||
      overlayOpen ||
      !conversationId ||
      previous.id !== conversationId ||
      !previous.busy ||
      busy
    ) {
      return
    }
    const readyId = conversationId
    void toast({
      id: `aida-ready-${readyId}`,
      title: t('studio.components.aida.result_ready'),
      description: (
        <button type="button" className="underline" onClick={() => openAidaOnChat(readyId)}>
          {t('studio.components.aida.showInChat')}
        </button>
      ),
    })
  }, [busy, conversationId, overlayOpen, t])

  return allowed && !overlayOpen && busy
}
